/**
 * AJAX pagination for the ks-whitepaper-list ACF module.
 *
 * Intercepts clicks on the .ks-whitepaper-list-pagination links and fetches
 * the next/previous page via WordPress admin-ajax. Falls back to a regular
 * link navigation if the global config is missing (e.g. script not localized).
 */

const SECTION_SELECTOR = '[data-ks-whitepaper-list]';
const INNER_SELECTOR = '[data-ks-whitepaper-list-inner]';
const PAGINATION_SELECTOR = '.ks-whitepaper-list-pagination';
const LINK_SELECTOR = '.page-numbers:not(.dots):not(.current)';

const getConfig = () => window.llm4kmuWhitepaperList || null;

const getPageFromHref = (href) => {
    if (!href) {
        return null;
    }

    const config = getConfig();
    const paramName = (config && config.pageParam) || 'wp_page';

    try {
        const url = new URL(href, window.location.origin);
        const fromQuery = url.searchParams.get(paramName);
        if (fromQuery) {
            const parsed = parseInt(fromQuery, 10);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        }
    } catch (error) {
        // Ignore invalid URLs; fall through to null.
    }

    return null;
};

const setLoading = (section, isLoading) => {
    section.classList.toggle('is-loading', Boolean(isLoading));
    section.setAttribute('aria-busy', isLoading ? 'true' : 'false');
};

const scrollSectionIntoView = (section) => {
    const header = document.querySelector('#fixed-menu, .fixed-top, header.fixed-top');
    const offset = header ? header.getBoundingClientRect().height : 0;
    const targetTop = section.getBoundingClientRect().top + window.scrollY - offset - 16;

    window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
    });
};

const updateUrl = (section, page) => {
    const config = getConfig();
    const paramName = (config && config.pageParam) || 'wp_page';

    try {
        const url = new URL(window.location.href);

        if (page && page > 1) {
            url.searchParams.set(paramName, String(page));
        } else {
            url.searchParams.delete(paramName);
        }

        url.hash = section.id ? `#${section.id}` : url.hash;

        window.history.replaceState({}, '', url.toString());
    } catch (error) {
        // Ignore; URL updating is a nice-to-have, not critical.
    }
};

const loadPage = async (section, page, { pushUrl = true, scroll = true } = {}) => {
    const config = getConfig();
    const inner = section.querySelector(INNER_SELECTOR);

    if (!config || !inner) {
        return false;
    }

    const postsPerPage = parseInt(section.dataset.postsPerPage || '6', 10) || 6;
    const elementId = section.dataset.elementId || section.id || 'ks-whitepaper-list';

    const body = new URLSearchParams();
    body.append('action', 'llm4kmu_whitepaper_list');
    body.append('nonce', config.nonce);
    body.append('page', String(page));
    body.append('posts_per_page', String(postsPerPage));
    body.append('element_id', elementId);

    setLoading(section, true);

    try {
        const response = await fetch(config.ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            body,
        });

        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();

        if (!payload || !payload.success || !payload.data || typeof payload.data.html !== 'string') {
            throw new Error('Invalid AJAX payload');
        }

        inner.innerHTML = payload.data.html;

        const nextPage = payload.data.current_page || page;
        section.dataset.currentPage = String(nextPage);

        if (pushUrl) {
            updateUrl(section, nextPage);
        }

        if (scroll) {
            scrollSectionIntoView(section);
        }

        return true;
    } catch (error) {
        return false;
    } finally {
        setLoading(section, false);
    }
};

const bindSection = (section) => {
    if (section.dataset.ksWhitepaperListBound === '1') {
        return;
    }
    section.dataset.ksWhitepaperListBound = '1';

    section.addEventListener('click', (event) => {
        const link = event.target.closest(`${PAGINATION_SELECTOR} ${LINK_SELECTOR}`);
        if (!link || !section.contains(link)) {
            return;
        }

        const href = link.getAttribute('href');
        const page = getPageFromHref(href);
        if (!page) {
            return;
        }

        const config = getConfig();
        if (!config) {
            // No localized config; let the browser handle the link normally.
            return;
        }

        event.preventDefault();
        loadPage(section, page).then((ok) => {
            if (!ok && href) {
                window.location.href = href;
            }
        });
    });
};

const initWhitepaperList = () => {
    const sections = document.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(bindSection);
};

export default initWhitepaperList;
